import PropTypes from 'prop-types'
import React, { useState, useRef, useEffect } from 'react'
import { CTabContent, CTabPane } from '@coreui/react'
import CIcon from '@coreui/icons-react'

import {
  CCard,
  CCardBody,
  CCardImage,
  CCardText,
  CCol,
  CRow,
  CTable,
  CTableBody,
  CTableRow,
  CTableDataCell,
  CModal,
  CModalBody,
  CButton,
} from '@coreui/react'

import {
  cilCompress,
  cilCloudDownload,
  cilMagnifyingGlass,
  cilVideo,
  cilCalendar,
  cilLocationPin,
  cilLayers,
} from '@coreui/icons'
import { calculatePriorityResult, calculateAssignedCompany, getPriorityTheme } from './Detail/DetailComponents/utils'
import { getCategoryByItem } from './Detail/CategoryConfig'

const DocsExample = (props) => {
  const { children, href, tabContentClassName, data, onFrameClick } = props
  
  // Extraer el primer ítem de la data
  const item = data && data.length > 0 ? data[0] : null;
  const itemConfig = getCategoryByItem(item) || { hasPriority: true, hasCompany: true }; // Fallback a true si no se reconoce
  const priority = item ? calculatePriorityResult(item) : 'Baja';
  const company = item ? calculateAssignedCompany(item) : 'GRUPO L';
  const priorityTheme = getPriorityTheme(priority);

  const [visible, setVisible] = useState(false)
  const [zoom, setZoom] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const [startPan, setStartPan] = useState({ x: 0, y: 0 })
  const imgRef = useRef(null)
  const containerRef = useRef(null)

  const zoomIn = () => setZoom((z) => Math.min(z + 0.25, 3));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.25, 0.5));
  const resetZoom = () => { setZoom(1); setTranslate({ x: 0, y: 0 }); }

  useEffect(() => {
    if (containerRef.current) containerRef.current.style.cursor = zoom > 1 ? 'grab' : 'default'
  }, [zoom])

  const clampTranslate = (x, y) => {
    const c = containerRef.current
    const img = imgRef.current
    if (!c || !img) return { x, y }
    const containerW = c.clientWidth
    const containerH = c.clientHeight
    const imgW = img.clientWidth // clientWidth ignores CSS transform scale
    const imgH = img.clientHeight
    const scaledW = imgW * zoom
    const scaledH = imgH * zoom

    let maxX = 0
    let maxY = 0
    if (scaledW > containerW) {
      maxX = (scaledW - containerW) / 2
    }
    if (scaledH > containerH) {
      maxY = (scaledH - containerH) / 2
    }

    const clampedX = Math.max(-maxX, Math.min(maxX, x))
    const clampedY = Math.max(-maxY, Math.min(maxY, y))
    return { x: clampedX, y: clampedY }
  }

  useEffect(() => {
    // Global listeners while panning so drag continues outside the container
    const onMouseMove = (e) => {
      if (!isPanning) return
      e.preventDefault()
      const desired = { x: e.clientX - startPan.x, y: e.clientY - startPan.y }
      const clamped = clampTranslate(desired.x, desired.y)
      setTranslate(clamped)
    }
    const onMouseUp = () => {
      if (!isPanning) return
      setIsPanning(false)
      if (containerRef.current) containerRef.current.style.cursor = 'grab'
    }
    const onTouchMove = (e) => {
      if (!isPanning) return
      e.preventDefault()
      const t = e.touches[0]
      const desired = { x: t.clientX - startPan.x, y: t.clientY - startPan.y }
      const clamped = clampTranslate(desired.x, desired.y)
      setTranslate(clamped)
    }
    const onTouchEnd = () => setIsPanning(false)

    if (isPanning) {
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
      document.addEventListener('touchmove', onTouchMove, { passive: false })
      document.addEventListener('touchend', onTouchEnd)
    }
    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.removeEventListener('touchmove', onTouchMove)
      document.removeEventListener('touchend', onTouchEnd)
    }
  }, [isPanning, startPan])


  // Función para obtener datos del servidor
  function truncateText(text, maxLength, truncationText = "...") {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + truncationText;
    }
    return text;
  }

  // Función para descargar la imagen
  const handleDownload = (imageUrl) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = 'imagen_descargada.jpg'; // Puedes cambiar el nombre del archivo aquí
    link.click();
  };

  const isValidValue = (val) => {
    if (val === null || val === undefined || val === '') return false;
    const s = val.toString().trim().toUpperCase();
    return s !== 'N/A' && s !== 'NONE' && s !== '0' && s !== 'NULL';
  };

  return (
    <>
      <div className="example">
        <CTabContent className={`rounded-bottom ${tabContentClassName ? tabContentClassName : ''}`}>
          <CTabPane className="preview" visible>
            <CCard className="shadow-sm border-0 h-100 overflow-hidden" style={{ borderRadius: '16px' }}>
              <div style={{ position: 'relative' }}>
                <CCardImage
                  orientation="top"
                  src={item?.url_frame || `${process.env.REACT_APP_API_BASE_URL}/images/casita.jpg`}
                  style={{ width: '100%', height: '170px', objectFit: 'cover', borderBottom: '1px solid #eee' }}
                  loading="lazy"
                />

                {/* Badges Flotantes sobre la imagen */}
                <div style={{ position: 'absolute', top: '12px', left: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {itemConfig.hasPriority && (
                    <span className="badge" style={{
                      backgroundColor: priorityTheme.bg,
                      color: priorityTheme.color,
                      padding: '5px 10px',
                      borderRadius: '8px',
                      fontSize: '0.6rem',
                      fontWeight: '800',
                      textTransform: 'uppercase',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                      backdropFilter: 'blur(4px)'
                    }}>
                      {priority}
                    </span>
                  )}
                  {itemConfig.hasCompany && (
                    <span className="badge bg-dark" style={{
                      padding: '5px 10px',
                      borderRadius: '8px',
                      fontSize: '0.6rem',
                      fontWeight: '800',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                      backdropFilter: 'blur(4px)',
                      opacity: 0.9
                    }}>
                      {company}
                    </span>
                  )}
                </div>

                <div className="d-flex gap-2" style={{ position: 'absolute', bottom: '12px', right: '12px' }}>
                  <button className='button-modal-frame' style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)', border: 'none' }}>
                    <CIcon icon={cilCloudDownload} title="Download file" size="sm" style={{ '--ci-primary-color': 'white' }}
                      onClick={() => handleDownload(item?.url_frame || `${process.env.REACT_APP_API_BASE_URL}/images/casita.jpg`)} />
                  </button>
                  <button className='button-modal-frame' style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)', border: 'none' }}>
                    <CIcon icon={cilCompress} size="sm" style={{ '--ci-primary-color': 'white' }} onClick={() => setVisible(!visible)} />
                  </button>
                </div>
              </div>

              <CCardBody className="p-4">
                <div className='d-flex flex-column gap-3'>

                  {/* Tipos: Específico y Principal */}
                  <div className="mb-1">
                    <div className="d-flex align-items-center mb-1">
                      <CIcon icon={cilLayers} size="sm" className="me-2 text-primary opacity-75" />
                      <span className="text-muted fw-bold" style={{ fontSize: '0.6rem', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Detección</span>
                    </div>
                    <div className="ps-0">
                      <h5 className="mb-1 fw-bold text-dark" style={{ fontSize: '1.05rem', lineHeight: '1.2' }}>
                        {item?.tipo_especifico || item?.tipo || 'Detección'}
                      </h5>
                      {isValidValue(item?.tipo) && item?.tipo !== item?.tipo_especifico && (
                        <div className="d-flex align-items-center gap-1 mt-1">
                          <span className="text-muted" style={{ fontSize: '0.75rem' }}>Tipo:</span>
                          <span className="fw-semibold text-primary" style={{ fontSize: '0.75rem' }}>{item.tipo}</span>
                        </div>
                      )}
                      {isValidValue(item?.tipo_principal) && (
                        <div className="d-flex align-items-center gap-1">
                          <span className="text-muted" style={{ fontSize: '0.75rem' }}>Principal:</span>
                          <span className="fw-semibold text-secondary" style={{ fontSize: '0.75rem' }}>{item.tipo_principal}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Icono de Ubicación y Fecha */}
                  <div className="d-flex flex-column gap-2 mt-1">
                    <div className="d-flex align-items-start text-dark" style={{ fontSize: '0.8rem' }}>
                      <CIcon icon={cilLocationPin} size="sm" className="me-2 text-primary mt-1" style={{ minWidth: '16px' }} />
                      <span className="fw-medium">
                        {item?.direccion || 'Sin dirección registrada'}
                      </span>
                    </div>

                    <div className="d-flex align-items-center text-muted" style={{ fontSize: '0.75rem' }}>
                      <CIcon icon={cilCalendar} size="sm" className="me-2 opacity-75" style={{ minWidth: '16px' }} />
                      <span>{item?.fecha_captura || 'Fecha no disponible'}</span>
                    </div>
                  </div>

                  {/* Button */}
                  <div className="mt-2 pt-2 border-top">
                    <CButton
                      color="primary"
                      className="w-100 fw-bold shadow-sm d-flex align-items-center justify-content-center"
                      style={{ borderRadius: '10px', padding: '10px', fontSize: '0.85rem', background: '#131133' }}
                      onClick={() => {
                        if (props.onOpenDetails) {
                          props.onOpenDetails();
                        }
                      }}
                    >
                      <CIcon icon={cilMagnifyingGlass} size="sm" className="me-2" />
                      Ver Detalles Completos
                    </CButton>
                  </div>

                </div>
              </CCardBody>
            </CCard>
          </CTabPane>
        </CTabContent>
      </div>
      <CModal
        className=''
        alignment="center"
        visible={visible}
        onClose={() => setVisible(false)}
        aria-labelledby="VerticallyCenteredExample"
        size="lg"
      >
        <CModalBody className=''>
          <CCard style={{ width: '48rem', height: '30 rem' }}>
            <div style={{ position: 'relative' }}>
              <div
                ref={containerRef}
                onMouseDown={(e) => {
                  if (zoom <= 1) return
                  e.preventDefault()
                  setIsPanning(true)
                  setStartPan({ x: e.clientX - translate.x, y: e.clientY - translate.y })
                  if (containerRef.current) containerRef.current.style.cursor = 'grabbing'
                }}
                onMouseLeave={() => { if (isPanning) setIsPanning(false) }}
                onTouchStart={(e) => {
                  if (zoom <= 1) return
                  const t = e.touches[0]
                  setIsPanning(true)
                  setStartPan({ x: t.clientX - translate.x, y: t.clientY - translate.y })
                }}
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', width: '100%', touchAction: 'none' }}>
                <img
                  ref={imgRef}
                  src={data && data.length > 0 && data[0].url_frame ? data[0].url_frame : `${process.env.REACT_APP_API_BASE_URL}/images/casita.jpg`}
                  alt="frame"
                  loading="lazy"
                  style={{
                    width: '100%',
                    maxWidth: '1000px',
                    height: 'auto',
                    transform: `translate(${translate.x}px, ${translate.y}px) scale(${zoom})`,
                    transition: isPanning ? 'none' : 'transform 150ms ease-in-out',
                    display: 'block',
                    cursor: zoom > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default'
                  }}
                />
              </div>
              <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '8px' }}>
                {/* <CButton size="sm" color="light" onClick={zoomOut} title="Zoom out">-</CButton> */}
                <CButton size="sm" color="light" onClick={resetZoom} title="Reset zoom">1x</CButton>
                <CButton size="sm" color="light" onClick={zoomIn} title="Zoom in">+</CButton>
              </div>
              <button className='button-modal-frame' style={{ position: 'absolute', bottom: '10px', right: '40px' }}>
                <CIcon icon={cilCloudDownload} title="Download file" style={{ '--ci-primary-color': 'white' }}
                  onClick={() => handleDownload(data && data.length > 0 && data[0].url_frame ? data[0].url_frame : `${process.env.REACT_APP_API_BASE_URL}/images/casita.jpg`)} />
              </button>
              <button className='button-modal-frame' style={{ position: 'absolute', bottom: '10px', right: '10px' }}>
                <CIcon icon={cilCompress} style={{ '--ci-primary-color': 'white' }} onClick={() => setVisible(!visible)} />
              </button>
            </div>

          </CCard>
        </CModalBody>

      </CModal>
    </>
  )
}

DocsExample.propTypes = {
  children: PropTypes.node,
  href: PropTypes.string,
  tabContentClassName: PropTypes.string,
  data: PropTypes.array, // Agrega esta línea para definir la prop data
  onFrameClick: PropTypes.func, // callback when frame number is clicked
}

export default React.memo(DocsExample)